/**
 * User role context provider.
 * Manages user role data and provides it to all components.
 * This ensures only one API call is made for role data, even when multiple components need it.
 */

import type { ReactNode } from 'react'
import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from 'react'
import { ENV } from '../config/env'
import { authenticatedFetch } from '../services/auth-interceptor'
import type { ApiResponseEnvelope } from '../services/api'
import { useAuthUser, useAuthStatus } from './AuthContext'

const API_BASE_URL = ENV.API_BASE_URL

export interface UserRoleResponse {
    user_id: string
    username: string
    role: {
        id: string
        level: number
        description: string
    }
}

export interface UserRoleContextType {
    role: string | null
    roleData: UserRoleResponse | null
    loading: boolean
    error: Error | null
    refetch: () => Promise<void>
}

const UserRoleContext = createContext<UserRoleContextType | undefined>(undefined)

export { UserRoleContext }

/**
 * Hook to access user role data from context.
 * This hook should be used instead of the old useUserRole hook.
 */
// Justified exception: Custom hook export (not a component) - React Refresh limitation
// eslint-disable-next-line react-refresh/only-export-components -- Justified: custom hook export
export const useUserRoleContext = (): UserRoleContextType => {
    const context = useContext(UserRoleContext)
    if (context === undefined) {
        throw new Error('useUserRoleContext must be used within a UserRoleProvider')
    }
    return context
}

interface UserRoleProviderProps {
    children: ReactNode
    skipFetch?: boolean
    /** Initial role ID for testing/Storybook (bypasses fetch). */
    initialRole?: string | null
    /** Initial role data for testing/Storybook (bypasses fetch). */
    initialRoleData?: UserRoleResponse | null
}

export const UserRoleProvider: React.FC<UserRoleProviderProps> = ({
    children,
    skipFetch = false,
    initialRole = null,
    initialRoleData = null,
}) => {
    const user = useAuthUser()
    const { status } = useAuthStatus()
    const [role, setRole] = useState<string | null>(initialRole)
    const [roleData, setRoleData] = useState<UserRoleResponse | null>(initialRoleData)
    const [loading, setLoading] = useState(!skipFetch && !initialRole)
    const [error, setError] = useState<Error | null>(null)

    // Track if there's a pending fetch to prevent duplicate requests
    const pendingFetchRef = useRef<Promise<void> | null>(null)

    const isSignedIn = status === 'signedIn'

    const fetchRole = useCallback(async () => {
        // Don't fetch if user is not authenticated
        if (!user || !isSignedIn) {
            setRole(null)
            setRoleData(null)
            setLoading(false)
            setError(null)
            return
        }

        // If there's already a pending fetch, wait for it
        if (pendingFetchRef.current) {
            await pendingFetchRef.current
            return
        }

        // Create the fetch promise and store it
        const fetchPromise = (async () => {
            try {
                setLoading(true)
                setError(null)

                const response = await authenticatedFetch(`${API_BASE_URL}/api/v1/auth/me/role`, {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                })

                if (!response.ok) {
                    if (response.status === 401 || response.status === 403) {
                        // User not authenticated or not authorized
                        if (process.env.NODE_ENV === 'development') {
                            console.warn(
                                '[UserRoleProvider] Unauthorized/Forbidden:',
                                response.status
                            )
                        }
                        setRole(null)
                        setRoleData(null)
                        setLoading(false)
                        return
                    }
                    const errorMessage = `Failed to fetch role: ${response.status}`
                    if (process.env.NODE_ENV === 'development') {
                        console.error('[UserRoleProvider]', errorMessage)
                    }
                    throw new Error(errorMessage)
                }

                const responseData =
                    (await response.json()) as ApiResponseEnvelope<UserRoleResponse>
                const data = 'data' in responseData ? responseData.data : responseData

                // Log for debugging (remove in production)
                if (process.env.NODE_ENV === 'development') {
                    console.log('[UserRoleProvider] Fetched role data:', data)
                }

                setRoleData(data)
                setRole(data.role.id)
            } catch (err) {
                const error = err instanceof Error ? err : new Error('Unknown error')
                if (process.env.NODE_ENV === 'development') {
                    console.error('[UserRoleProvider] Error fetching role:', error)
                }
                setError(error)
                setRole(null)
                setRoleData(null)
            } finally {
                setLoading(false)
                // Clear the pending fetch reference
                pendingFetchRef.current = null
            }
        })()

        pendingFetchRef.current = fetchPromise
        await fetchPromise
    }, [user, isSignedIn]) // user and isSignedIn are the actual dependencies

    // Fetch role when user changes or becomes authenticated
    useEffect(() => {
        if (!skipFetch) {
            void fetchRole()
        }
    }, [fetchRole, skipFetch])

    // Clear role data when user logs out
    useEffect(() => {
        if (!user || !isSignedIn) {
            setRole(null)
            setRoleData(null)
            setError(null)
            setLoading(false)
        }
    }, [user, isSignedIn])

    // Memoized context value
    const value = useMemo<UserRoleContextType>(
        () => ({
            role,
            roleData,
            loading,
            error,
            refetch: fetchRole,
        }),
        [role, roleData, loading, error, fetchRole]
    )

    return <UserRoleContext.Provider value={value}>{children}</UserRoleContext.Provider>
}
